import { Link } from 'react-router';

import { menu } from '@/app/navegacion';
import { puede, useMe } from '@/auth/queries';
import { Encabezado } from '@/components/Estados';
import { Kpi } from '@/components/Graficas';
import { useResumenInicio, type Resumen } from '@/features/reportes/queries';
import { mxn, numero } from '@/lib/formato';

function cambio(actual: number, anterior: number): string {
  if (anterior === 0) return actual > 0 ? 'sin datos del mes pasado' : 'igual que el mes pasado';
  const d = Math.round(((actual - anterior) / anterior) * 100);
  return `${d > 0 ? '+' : ''}${d}% vs. mes pasado`;
}

/** KPIs de las secciones que el staff puede ver (la API solo manda esos bloques). */
function Indicadores({ r }: { r: Resumen }) {
  const kpis: { k: string; etiqueta: string; valor: string; detalle?: string; a?: string }[] = [];
  if (r.alumnos) {
    kpis.push(
      {
        k: 'a1',
        etiqueta: 'Alumnos con licencia vigente',
        valor: numero(r.alumnos.withActiveMembership),
        detalle: `${numero(r.alumnos.total)} cuentas en total`,
        a: '/alumnos',
      },
      {
        k: 'a2',
        etiqueta: 'Registros este mes',
        valor: numero(r.alumnos.newThisMonth),
        detalle: cambio(r.alumnos.newThisMonth, r.alumnos.newLastMonth),
      },
    );
    if (r.alumnos.suspended > 0)
      kpis.push({
        k: 'a3',
        etiqueta: 'Suspendidos',
        valor: numero(r.alumnos.suspended),
        a: '/alumnos?status=suspended',
      });
  }
  if (r.pagos)
    kpis.push(
      {
        k: 'p1',
        etiqueta: 'Ventas este mes',
        valor: mxn(r.pagos.totalThisMonth),
        detalle: `${numero(r.pagos.paidThisMonth)} pagos · ${cambio(Number(r.pagos.totalThisMonth), Number(r.pagos.totalLastMonth))}`,
        a: '/pagos',
      },
      ...(r.pagos.pending > 0
        ? [{ k: 'p2', etiqueta: 'Pagos pendientes', valor: numero(r.pagos.pending), a: '/pagos' }]
        : []),
    );
  if (r.tienda)
    kpis.push({
      k: 't1',
      etiqueta: 'Licencias en la tienda',
      valor: numero(r.tienda.inStore),
      detalle: `${numero(r.tienda.activeMemberships)} activas · ${numero(r.tienda.activeCoupons)} cupones vigentes`,
      a: '/tienda/licencias',
    });
  if (r.preguntas)
    kpis.push({
      k: 'q1',
      etiqueta: 'Reportes de casos abiertos',
      valor: numero(r.preguntas.openReports),
      detalle: `${numero(r.preguntas.baseQuestions)} casos base`,
      a: '/preguntas/reportes',
    });
  if (r.ia && (r.ia.inReview > 0 || r.ia.queued > 0))
    kpis.push({
      k: 'i1',
      etiqueta: 'Casos con IA por revisar',
      valor: numero(r.ia.inReview),
      detalle: r.ia.queued > 0 ? `${numero(r.ia.queued)} tareas en cola` : undefined,
      a: '/ia/revision',
    });
  if (r.contenido) {
    kpis.push({
      k: 'c1',
      etiqueta: 'Clases visibles',
      valor: numero(r.contenido.publishedClasses),
      detalle: `${numero(r.contenido.libraryItems)} elementos en la biblioteca`,
      a: '/contenido/clases',
    });
    if (r.contenido.classesWithoutTopic > 0)
      kpis.push({
        k: 'c2',
        etiqueta: 'Clases visibles sin tema',
        valor: numero(r.contenido.classesWithoutTopic),
        detalle: 'el alumno no las ve',
        a: '/contenido/temario',
      });
    if (r.contenido.unassignedItems > 0)
      kpis.push({
        k: 'c3',
        etiqueta: 'Contenido sin licencia',
        valor: numero(r.contenido.unassignedItems),
        a: '/contenido?unassigned=true',
      });
  }
  if (r.reportes)
    kpis.push({
      k: 'r1',
      etiqueta: 'Opiniones este mes',
      valor: numero(r.reportes.feedbackThisMonth),
      detalle: `${numero(r.reportes.securityEventsThisMonth)} eventos de seguridad`,
      a: '/reportes/opiniones',
    });
  if (kpis.length === 0) return null;
  return (
    <section className="mb-6 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
      {kpis.map((x) => (
        <Kpi
          key={x.k}
          etiqueta={x.etiqueta}
          valor={x.valor}
          detalle={x.detalle}
          enlace={
            x.a && (
              <Link to={x.a} className="mt-1 inline-block text-xs font-semibold">
                Ver →
              </Link>
            )
          }
        />
      ))}
    </section>
  );
}

/** Inicio: KPIs y accesos según las secciones del staff. */
export function InicioPage() {
  const resumen = useResumenInicio();
  const { data: me } = useMe();
  const grupos = menu.filter((g) => g.seccion && puede(me, g.seccion, 'ver'));
  const nombre = me?.name.split(' ')[0] ?? '';
  return (
    <>
      <Encabezado
        titulo={`Hola, ${nombre}`}
        descripcion="Lo más importante de hoy y las secciones del panel a las que tienes acceso."
      />
      {resumen.data && <Indicadores r={resumen.data} />}
      {grupos.length === 0 ? (
        <section className="panel-vidrio max-w-xl">
          <p className="font-bold">Tu cuenta todavía no tiene secciones asignadas.</p>
          <p className="mt-1 text-texto-2">Pide acceso a un administrador del panel.</p>
        </section>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {grupos.map((g) => {
            const Icono = g.icono;
            const nivel = me?.isSuperadmin
              ? 'Superadmin'
              : me?.sections.find((s) => s.section === g.seccion)?.level === 'editar'
                ? 'Edición'
                : 'Consulta';
            return (
              <section key={g.etiqueta} className="card-vidrio p-5">
                <header className="mb-3 flex items-center gap-3">
                  <span className="grid h-10 w-10 place-items-center rounded-xl bg-azul-600 text-white">
                    <Icono size={20} aria-hidden />
                  </span>
                  <div>
                    <h2 className="font-extrabold">{g.etiqueta}</h2>
                    <p className="text-xs text-texto-3">{nivel}</p>
                  </div>
                </header>
                <ul className="space-y-1 text-sm">
                  {g.items.map((i) => (
                    <li key={i.ruta}>
                      {i.pronto ? (
                        <span className="text-texto-3">{i.etiqueta} · pronto</span>
                      ) : (
                        <Link to={i.ruta} className="font-semibold">
                          {i.etiqueta}
                        </Link>
                      )}
                    </li>
                  ))}
                </ul>
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}
